import type { Tab } from './index';
import { esc } from '../utils';
import { fetchSheet } from '../worker';

interface InstitutionalRow {
  Ticker?:      string;
  TickerURL?:   string;
  CompanyName?: string;
  CardSubtitle?:string;
  Investor?:    string;
  InvestorType?:string;
  Stake?:       string;
  StakeColor?:  string;
  Notes?:       string;
}

export class InstitutionalTab implements Tab {
  readonly id = 'institutional';
  private loaded = false;

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void { /* no interactive controls */ }

  render(): void {
    if (!this.loaded) this.load();
  }

  private async load(): Promise<void> {
    const container = this.el('institutional-cards');
    container.innerHTML = '<div style="grid-column:1/-1;text-align:center;color:var(--text3);padding:40px;letter-spacing:0.1em;font-size:11px;">LOADING…</div>';
    try {
      const data = await fetchSheet<InstitutionalRow>('Institutional');
      const rows = data.rows ?? [];
      if (!rows.length) {
        container.innerHTML = '<div style="grid-column:1/-1;text-align:center;color:var(--text3);padding:40px;font-size:12px;">No data found in Institutional sheet tab.</div>';
        return;
      }
      container.innerHTML = this.buildCards(rows);
      this.loaded = true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      container.innerHTML =
        `<div style="grid-column:1/-1;text-align:center;padding:40px;">` +
        `<span style="color:var(--red);font-size:12px;">Could not load Institutional Investors.</span><br>` +
        `<span style="color:var(--text3);font-size:10px;">${esc(msg)}</span></div>`;
    }
  }

  private buildCards(rows: InstitutionalRow[]): string {
    // Group by Ticker, preserving insertion order
    const order: string[] = [];
    const groups = new Map<string, { first: InstitutionalRow; rows: InstitutionalRow[] }>();

    for (const r of rows) {
      const t = r.Ticker ?? '';
      if (!groups.has(t)) { groups.set(t, { first: r, rows: [] }); order.push(t); }
      groups.get(t)!.rows.push(r);
    }

    return order.map(ticker => {
      const g     = groups.get(ticker)!;
      const first = g.first;
      const badge = first.TickerURL
        ? `<a href="${esc(first.TickerURL)}" target="_blank" rel="noopener">${esc(ticker)}</a>`
        : esc(ticker);

      const trs = g.rows.map(r => {
        const sc = r.StakeColor === 'yes' ? 'yes' : r.StakeColor === 'warn' ? 'warn' : 'text-muted';
        return `<tr>
          <td><strong>${esc(r.Investor ?? '—')}</strong></td>
          <td class="text-muted">${esc(r.InvestorType ?? '—')}</td>
          <td class="${sc}">${esc(r.Stake ?? '—')}</td>
          <td class="notes-cell">${esc(r.Notes ?? '—')}</td>
        </tr>`;
      }).join('');

      return `<div class="investor-card">
        <div class="investor-card-header"><div>
          <div class="investor-card-title">
            <span class="ticker-badge" style="margin-right:8px">${badge}</span>${esc(first.CompanyName ?? '')}
          </div>
          <div class="investor-card-sub">${esc(first.CardSubtitle ?? '')}</div>
        </div></div>
        <table style="width:100%">
          <thead><tr><th>Investor</th><th>Type</th><th>Stake</th><th>Notes</th></tr></thead>
          <tbody>${trs}</tbody>
        </table>
      </div>`;
    }).join('');
  }
}
